import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import * as R from 'ramda';

import { withTranslation } from 'react-i18next';

import red from '@mui/material/colors/red';
import green from '@mui/material/colors/green';

import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import { ModalPreview } from '../utils/modalHelpers';

import { returnField } from '../utils/formHelpers';

import {
  updateProject,
  updateProjectLockedState,
} from '../store/actions/projectActions';
import NoAccess from '../components/navigation/NoAccess';

import FileManagement from '../components/fileManagement/FileManagement';
import CardWrapper from '../components/common/CardWrapper';

import RFILetter from '../components/common/RFILetter';
import { useTheme } from '@mui/material';

import { Field } from '../components/Field/Field.js'
import { fastDeepEqual } from '../utils/object.js'


const inlineStyles = theme => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    color: '#000',
  },
  tabRoot: {},
  button: {
    margin: theme.spacing(1),
  },
  leftIcon: {
    marginRight: theme.spacing(1),
  },
  rightIcon: {
    marginLeft: theme.spacing(1),
  },
  iconSmall: {
    fontSize: 20,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 200,
  },
  selectEmpty: {
    marginTop: theme.spacing(1) * 2,
  },
  colorSwitchBase: {
    color: green[500],
    '& + $colorBar': {
      backgroundColor: green[500],
    },
  },
  colorBar: {},
  colorChecked: {
    color: red[500],
    '& + $colorBar': {
      backgroundColor: red[500],
    },
  },
});

let ProjectInfo = ({
  admin,
  pid,
  projects,
  updateProjectLockedState,
  t,
  tenRepArr,
  tenrepsNames,
  updateProject,
  initialValues,
}) => {
  const [formValues, setFormValues] = useState(structuredClone(initialValues))
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    setFormValues(initialValues)
  }, [initialValues])

  const pristine = fastDeepEqual(initialValues, formValues)

  const handleSubmitCheck = useCallback(async event => {
    event.preventDefault()

    setSubmitting(true)

    const hasNewStatus =
      formValues.projectInfo.status !==
      initialValues.projectInfo.status

    await updateProject(formValues, hasNewStatus);

    setSubmitting(false)
  }, [
    formValues,
    initialValues,
    updateProject
  ])

  const theme = useTheme();
  const classes = inlineStyles(theme);
  return admin ? (
    <CardWrapper
      name={
        !pid
          ? t('projectDetailView.createNewProject')
          : t('projectDetailView.projectDetails')
      }
      headline
    >
      {pid && (
        <FormControlLabel
          control={
            <Switch
              checked={R.path([pid, 'projectInfo', 'locked'], projects)}
              onChange={() =>
                updateProjectLockedState(
                  pid,
                  !R.path([pid, 'projectInfo', 'locked'], projects)
                )
              }
              sx={{
                '&.MuiSwitch-root .MuiSwitch-switchBase':
                  classes.colorSwitchBase,
                '&.MuiSwitch-root .Mui-checked': classes.colorChecked,
              }}
            />
          }
          label={
            R.path([pid, 'projectInfo', 'locked'], projects)
              ? t('projectDetailView.projectLocked')
              : t('projectDetailView.projectUnLocked')
          }
        />
      )}
      <form onSubmit={handleSubmitCheck}>
        <CardWrapper
          name={t('projectDetailView.generalInformation')}
          headline
          transparent
        >
          <Grid
            container
            spacing={4}
            direction="row"
            alignContent="flex-start"
            alignItems="flex-start"
          >
            <Grid item sm={12} md={6}>
              <Field
                component={returnField('text')}
                fullWidth
                label={t('projectDetailView.nameLabel')}
                name="projectInfo.name"
                required
                setValue={setFormValues}
                type="text"
                value={formValues.projectInfo.name}
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <Field
                component={returnField('text')}
                fullWidth
                label={t('projectDetailView.clientForTenrepLabel')}
                name="clientVisibleToTenrepOnly"
                required
                setValue={setFormValues}
                type="text"
                value={formValues.clientVisibleToTenrepOnly}
              />
            </Grid>
            {pid && (
              <Grid item sm={12} md={6}>
                <Field
                  component={returnField('text')}
                  fullWidth
                  label={t('projectDetailView.propertyTypeLabel')}
                  name="projectInfo.propertyType"
                  required
                  setValue={setFormValues}
                  type="text"
                  value={formValues.projectInfo.propertyType}
                />
              </Grid>
            )}
            {pid && (
              <Grid item sm={12} md={6}>
                <Field
                  adornment="m2"
                  component={returnField('text')}
                  fullWidth
                  label={t('projectDetailView.sizeLabel')}
                  name="projectInfo.size"
                  required
                  setValue={setFormValues}
                  type="text"
                  value={formValues.projectInfo.size}
                />
              </Grid>
            )}
            {pid && (
              <Grid item sm={12} md={6}>
                <Field
                  component={returnField('text')}
                  fullWidth
                  label={t('projectDetailView.areaLabel')}
                  name="projectInfo.area"
                  required
                  setValue={setFormValues}
                  type="text"
                  value={formValues.projectInfo.area}
                />
              </Grid>
            )}
            <Grid item sm={12} md={6}>
              <Field
                component={returnField('selectObj')}
                fullWidth
                id="projectInfo.contactPerson"
                label={t('projectDetailView.contactPersonLabel')}
                name="projectInfo.contactPerson"
                required
                selOpts={tenRepArr}
                setValue={setFormValues}
                type="select"
                value={formValues.projectInfo.contactPerson}
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <Field
                component={returnField('select')}
                fullWidth
                id="projectInfo.responsible"
                label={t('projectDetailView.responsibleLabel')}
                multiple
                name="projectInfo.responsible"
                required
                selOpts={tenrepsNames}
                setValue={setFormValues}
                type="text"
                value={formValues.projectInfo.responsible}
              />
            </Grid>
          </Grid>
        </CardWrapper>
        <CardWrapper
          name={t('projectDetailView.rfiLetterContents')}
          headline
          transparent
        >
          <Grid
            container
            spacing={4}
            direction="row"
            alignContent="flex-start"
            alignItems="flex-start"
          >
            <Grid item sm={12} md={6}>
              <Field
                component={returnField('text')}
                fullWidth
                label={t('projectDetailView.detailsHeadlineLabel')}
                name="projectInfo.detailsHeadline"
                required
                setValue={setFormValues}
                type="text"
                value={formValues.projectInfo.detailsHeadline}
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <Field
                component={returnField('text')}
                fullWidth
                label={t('projectDetailView.detailsSubheaderLabel')}
                name="projectInfo.detailsSubheader"
                required
                setValue={setFormValues}
                type="text"
                value={formValues.projectInfo.detailsSubheader}
              />
            </Grid>
            {pid && (
              <Grid item sm={12} md={6}>
                <Field
                  component={returnField('textarea')}
                  fullWidth
                  label={t('projectDetailView.detailsDescriptionLabel')}
                  name="projectInfo.detailsDescription"
                  rows={4}
                  setValue={setFormValues}
                  type="text"
                  value={formValues.projectInfo.detailsDescription}
                />
              </Grid>
            )}
            {pid && (
              <Grid item sm={12} md={6}>
                <Field
                  component={returnField('textarea')}
                  fullWidth
                  label={t('projectDetailView.detailsSpaceLabel')}
                  name="projectInfo.detailsSpace"
                  rows={4}
                  setValue={setFormValues}
                  type="text"
                  value={formValues.projectInfo.detailsSpace}
                />
              </Grid>
            )}
            {pid && (
              <Grid item sm={12} md={6}>
                <Field
                  component={returnField('text')}
                  fullWidth
                  label={t('projectDetailView.detailsLocationLabel')}
                  name="projectInfo.detailsLocation"
                  setValue={setFormValues}
                  type="text"
                  value={formValues.projectInfo.detailsLocation}
                />
              </Grid>
            )}
            {pid && (
              <Grid item sm={12} md={6}>
                <Field
                  component={returnField('text')}
                  fullWidth
                  label={t('projectDetailView.detailsTimelineLabel')}
                  name="projectInfo.detailsTimeline"
                  setValue={setFormValues}
                  type="text"
                  value={formValues.projectInfo.detailsTimeline}
                />
              </Grid>
            )}
            {pid && (
              <Grid item sm={12} md={6}>
                <Field
                  component={returnField('text')}
                  fullWidth
                  label={t('projectDetailView.detailsRentalPeriodLabel')}
                  name="projectInfo.detailsRentalPeriod"
                  setValue={setFormValues}
                  type="text"
                  value={formValues.projectInfo.detailsRentalPeriod}
                />
              </Grid>
            )}
            <Grid item sm={12} md={6}>
              <Field
                component={returnField('text')}
                fullWidth
                label={t('projectDetailView.clientLabel')}
                name="projectInfo.client"
                required
                setValue={setFormValues}
                type="text"
                value={formValues.projectInfo.client}
              />
            </Grid>
            {pid && (
              <Grid item sm={12} md={6}>
                <Field
                  component={returnField('editor')}
                  fullWidth
                  label={t('projectDetailView.detailsOtherLabel')}
                  name="projectInfo.detailsOther"
                  setValue={setFormValues}
                  type="text"
                  value={formValues.projectInfo.detailsOther}
                />
              </Grid>
            )}
            {pid && (
              <Grid item sm={12} md={6}>
                <FileManagement
                  allowMultipleFiles
                  collection="projects"
                  documentId={pid}
                  files={projects[pid].fileList}
                  setValue={setFormValues}
                />
              </Grid>
            )}
            <Grid item sm={12} md={6}>
              <Field
                component={returnField('select')}
                disabled={!pid}
                fullWidth
                id="projectInfo.status"
                label="Status"
                name="projectInfo.status"
                required
                selOpts={[
                  'Draft',
                  'Round 1',
                  'Round 2',
                  'Round 3',
                  'Round 4',
                  'Round 5',
                  'Round 6',
                  'Closed',
                  'Archived'
                ]}
                setValue={setFormValues}
                translateOptions
                type="text"
                value={formValues.projectInfo.status}
              />
            </Grid>
            {pid && (
              <Grid item sm={12} md={6}>
                <Field
                  component={returnField('date')}
                  fullWidth
                  id="projectInfo.deadline"
                  label={t('projectDetailView.deadlineLabel')}
                  name="projectInfo.deadline"
                  required
                  setValue={setFormValues}
                  type="date"
                  value={formValues.projectInfo.deadline || ''}
                />
              </Grid>
            )}
            <Grid item sm={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={classes.button}
                disabled={submitting || pristine}
              >
                {t('projectDetailView.saveProject')}
                <Icon sx={classes.rightIcon}>cloud_upload</Icon>
              </Button>
            </Grid>
          </Grid>
        </CardWrapper>
      </form>
      <ModalPreview
        modalTitle="Preview"
        modalDescription="Preview RFI Letter"
        openState={modalOpen}
        buttonSx={classes.button}
        iconSx={classes.rightIcon}
        onCancelClick={() => setModalOpen(false)}
        cancelButtonText={t('common.close')}
        submitting={submitting}
      >
        <Typography
          align="center"
          variant="heading"
          color="primary"
          component="h1"
          gutterBottom
        >
          RFI LETTER
        </Typography>
        <RFILetter
          detailsClient={R.path(['projectInfo', 'client'], formValues)}
          detailsDescription={R.path(
            ['projectInfo', 'detailsDescription'],
            formValues
          )}
          detailsHeadline={R.path(
            ['projectInfo', 'detailsHeadline'],
            formValues
          )}
          detailsSubheader={R.path(
            ['projectInfo', 'detailsSubheader'],
            formValues
          )}
          detailsSpace={R.path(
            ['projectInfo', 'detailsSpace'],
            formValues
          )}
          detailsLocation={R.path(
            ['projectInfo', 'detailsLocation'],
            formValues
          )}
          detailsTimeline={R.path(
            ['projectInfo', 'detailsTimeline'],
            formValues
          )}
          detailsRentalPeriod={R.path(
            ['projectInfo', 'detailsRentalPeriod'],
            formValues
          )}
          detailsOther={R.path(
            ['projectInfo', 'detailsOther'],
            formValues
          )}
          deadline={R.path(['projectInfo', 'deadline'], formValues)}
          contactPersonInfo={R.path(
            ['projectInfo', 'contactPersonInfo'],
            formValues
          )}
          pid={pid}
          files={R.path(['fileList'], projects[pid])}
          preview
        />
      </ModalPreview>
      {pid && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => setModalOpen(true)}
        >
          {t('projectDetailView.previewRFILetter')}
        </Button>
      )}
    </CardWrapper>
  ) : (
    <NoAccess />
  );
};

const mapStateToProps = (state, ownProps) => {
  const projects = R.path(['project', 'projects'], state);
  const attr = R.path(['attr', 'attr'], state);
  const pid = R.path(['pid'], ownProps);
  const users = R.path(['user', 'users'], state);

  // Inject client data to project data from separate reducer
  if (pid && projects[pid]) {
    projects[pid].clientVisibleToTenrepOnly = R.path(
      ['client', 'clients', pid, 'client'],
      state
    );
  }

  // get default attr
  const filterAttr = n => n.default;
  const filterBasicInfo = n => n.fieldCategory === 'basicInfo';
  const filterSpaceInfo = n => n.fieldCategory === 'spaceInfo';
  const defaultAttr = R.filter(filterAttr, attr);
  const defaultBasicInfo = R.filter(filterBasicInfo, defaultAttr);
  const defaultSpaceInfo = R.filter(filterSpaceInfo, defaultAttr);
  const basicInfoArr = R.keys(defaultBasicInfo).map(key => attr[key]);
  const spaceInfoArr = R.keys(defaultSpaceInfo).map(key => attr[key]);

  // get admin users
  const filterAdmins = n => n.mgmt.roles.admin;
  const tenReps = R.filter(filterAdmins, users);
  const tenRepArr = R.keys(tenReps).map(key => tenReps[key]);

  const tenrepsNames = [];
  const prependKeyAndDouble = num =>
    tenrepsNames.push(R.path(['profile', 'firstname'], num));
  R.mapObjIndexed(prependKeyAndDouble, tenReps);

  const initValues = {
    clientVisibleToTenrepOnly: '',
    projectInfo: {
      area: '',
      name: '',
      responsible: [],
      client: '',
      detailsHeadline: '',
      detailsSubheader: '',
      detailsDescription: '',
      detailsSpace: '',
      detailsLocation: '',
      detailsTimeline: '',
      detailsRentalPeriod: '',
      detailsOther: '',
      status: 'Draft',
      propertyType: '',
      contactPerson: '',
      size: 0,
      locked: false,
    },
    attr: {
      basicInfo: basicInfoArr,
      spaceInfo: spaceInfoArr,
      economicInfo: [],
      parking: [],
      realEstateServices: [],
      realEstateTechnical: [],
      risks: [],
      additionalInfo: [],
    },
    system: {
      createdDate: '',
    },
    users: {},
  };

  return {
    initialValues: pid ? projects[pid] : initValues,
    projects,
    admin: R.path(['user', 'admin'], state),
    tenRepArr,
    tenrepsNames
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateProject: (values, hasNewStatus) =>
    dispatch(
      updateProject(
        values,
        hasNewStatus,
        R.path(['pid'], ownProps),
        R.path(['navigate'], ownProps),
        R.path(['t'], ownProps)
      )
    ),
  updateProjectLockedState: (pid, checked) =>
    dispatch(updateProjectLockedState(pid, checked, R.path(['t'], ownProps))),
});

ProjectInfo = connect(
  mapStateToProps,
  mapDispatchToProps
)(ProjectInfo);

export default withTranslation('translations')(ProjectInfo);
